/**
 * src/components/layout/StaleStateBanner.jsx
 *
 * App-shell banner shown when the background state refresh has failed every
 * one of its backoff retries, so the app is knowingly rendering a snapshot it
 * could not bring up to date (GH #6393).
 *
 * Why it is not raised on the first failure: the overwhelmingly common cause
 * is a cold Function App answering 503 for a few seconds after a deploy, which
 * resolves itself. A banner on every cold start is a banner people learn to
 * ignore, which would leave a real outage less visible than it is today, not
 * more. usePersistence.js retries quietly first and fires
 * 'seasuite:state-stale' only once those retries are exhausted; it fires
 * 'seasuite:state-fresh' when a later refresh finally lands, which clears this.
 *
 * Deliberately NOT dismissible. Unlike a write error — which describes an event
 * that has already happened and can be acknowledged — this describes a
 * condition that is still true: every edit made while it shows is being made
 * against a copy the app knows may be behind. It goes away when the condition
 * does.
 */

import { useState, useEffect } from 'react';

export default function StaleStateBanner() {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const onStale = () => setStale(true);
    const onFresh = () => setStale(false);
    window.addEventListener('seasuite:state-stale', onStale);
    window.addEventListener('seasuite:state-fresh', onFresh);
    return () => {
      window.removeEventListener('seasuite:state-stale', onStale);
      window.removeEventListener('seasuite:state-fresh', onFresh);
    };
  }, []);

  if (!stale) return null;

  return (
    <div
      role="status"
      style={{
        background: 'var(--warn-bg, rgba(245,158,11,0.10))',
        borderBottom: '1px solid var(--warn)',
        color: 'var(--warn)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 20px',
        fontSize: '13px',
        fontFamily: 'var(--font-mono)',
        zIndex: 200,
        flexShrink: 0,
        pointerEvents: 'auto',
      }}
    >
      <span>
        <span style={{ fontWeight: 600 }}>Working from a stale copy</span>
        {' — '}
        the app cannot reach the server to refresh this page&apos;s data, so someone else&apos;s
        changes may not be shown. Your own edits are still being saved. Reconnecting…
      </span>
    </div>
  );
}
